/**
 * Login
 *
 * @module app/components/Login
 */
import _ from 'lodash';
import BetSlipAdapter from './BetSlipAdapter';
import DeferredDependencies from './DeferredDependencies';
import DeviceDetector from './DeviceDetector';
import jQuery from 'jquery';
import MessageBusTopics from '../../common/MessageBusTopics';
import URLProvider from './URLProvider';

/**
 * wrapper around the window.whx object from OB. Set inside the waitForGlobalVar function
 *
 * @type    {Object}
 */
var whx = null;

/**
 * Variable to encapsulate the PreferencesProvider
 *
 * @type {Object|null}
 */
var PreferencesProvider = null;

/**
 * String for the key for the remembered username to be used in the PreferencesProvider
 *
 * @type {String}
 */
var rememberedUsernamePrefsKey = 'RememberedUsername';

/**
 * String for the CUST get balance action.
 *
 * @type {String}
 */
var balanceAction = 'GetBalance';

/**
 * String for the CUST get balance action, when endpoint reached over https.
 *
 * @type {String}
 */
var balanceSslAction = 'GetSslBalance';

/**
 * Method to inject dependencies (most likely to be used in init method)
 *
 * @param   {Object}    PreferencesProvider
 *
 * @returns {void}
 *
 * @private
 */
var injectDependencies = function (PrefProvider) {

    PreferencesProvider = PrefProvider;
};

/**
 * Method to remember a username (i.e. they checked 'remember me' when logging in)
 *
 * @param {String}    username
 *
 * @returns {void}
 *
 * @private
 */
var rememberMeAction = function (username) {

    if (username !== '') {
        saveRememberedUsername(username);
    } else {
        clearRememberedUsername();
    }
};

/**
 * Method to save a username to the PreferencesProvider
 *
 * @param   {String}    username
 *
 * @returns {void}
 *
 * @private
 */
var saveRememberedUsername = function (username) {

    PreferencesProvider.setPreference(rememberedUsernamePrefsKey, username);
};

/**
 * Method to clear a username from the Preferences provider
 *
 * @returns {void}
 *
 * @private
 */
var clearRememberedUsername = function () {

    PreferencesProvider.setPreference(rememberedUsernamePrefsKey, '');
};

/**
 * Get the remembered username
 *
 * @returns {String}
 *
 * @private
 */
var getRememberedUsername = function () {

    return PreferencesProvider.getPreference(rememberedUsernamePrefsKey);
};

/**
 * Method to check if this user is remembered
 *
 * @returns {boolean}
 *
 * @private
 */
var isRemembered = function () {

    //is not undefined nor is empty string
    return (
        typeof getRememberedUsername() !== 'undefined' &&
        getRememberedUsername() !== ''
    );
};

/**
 * Method to update the username field in the login form
 *
 * @param   {String}  username
 *
 * @returns {void}
 *
 * @private
 */
var updateUsernameField = function (username) {

    jQuery('input[name="username"]')
        .val(username);
    jQuery('input#rememberMe')
        .prop('checked', true);
};

/**
 * Method to check if the the current user is remembers and if so,
 *      get there username and insert it into the login box
 *
 * @returns {void}
 *
 * @private
 */
var updateLoginForm = function () {

    if (isRemembered()) {
        updateUsernameField(getRememberedUsername());
    }
};

var setDimension = function (dimension, value, container) {

    if (typeof value !== 'undefined') {
        container[dimension](value);
    }
};

/**
 * Check if the user is logged
 *
 * @returns {Boolean}
 *
 * @private
 */
var userLogged = function () {

    return (whx && whx.hasOwnProperty('authenticated') && whx.authenticated === 'Y');
};

/**
 * Checks if object contains field
 *
 * @param   {Object} object
 * @param   {String} field
 *
 * @returns {boolean}
 *
 * @private
 */
var validateField = function (object, field) {

    return object.hasOwnProperty(field);
};

/**
 * Validate non authenticated fields
 *
 * @returns {Boolean}
 *
 * @private
 */
var validateNonAuthenticated = function () {

    return (validateField(whx.data, 'obFormAction') &&
        validateField(whx.data, 'obFormHidden') &&
        validateField(whx.data, 'obPasswordName') &&
        validateField(whx.data, 'obUsernameName'));
};

/**
 * Validates Auth fields
 *
 * @returns {Boolean}
 *
 * @private
 */
var validateAuthenticated = function () {

    return (validateField(whx.data, 'obAccountNumber') &&
        validateField(whx.data, 'obAccountURL') &&
        validateField(whx.data, 'obBalanceAmount') &&
        validateField(whx.data, 'obBalanceCurrency') &&
        validateField(whx.data, 'obDepositURL') &&
        validateField(whx.data, 'obMessageURL'));
};

/**
 *  Validate WHX Data
 *
 *  @returns {Boolean}
 *
 *  @private
 */
var validateWHXData = function () {

    if (userLogged()) {
        return validateAuthenticated();
    } else {
        return validateNonAuthenticated();
    }
};

/**
 * Validates the content of the WHX object from OB
 *
 * @returns {boolean}
 *
 * @private
 */
var validateWhx = function () {

    if (validateField(whx, 'data')) {
        return validateWHXData();
    }

    throw 'WHX object from OB not valid';
};

/**
 * Hides the non logged in dropdown
 *
 * @param content
 *
 * @returns {void}
 *
 * @private
 */
var hideLoggedBox = function (content) {

    content.find('#not-logged')
        .hide();
    content.find('#logged')
        .show();
    jQuery('#header').find('a[data-type="iframe"]:not(#joinLink,#passwordRecoveryLink)')
        .show();
};

/**
 * Shows the non logged in dropdown
 *
 * @param content
 *
 * @returns {void}
 *
 * @private
 */
var showLoggedBox = function (content) {

    content.find('#not-logged')
        .show();
    content.find('#logged')
        .hide();
    jQuery('#header').find('a[data-type="iframe"]:not(#joinLink,#passwordRecoveryLink)')
        .hide();
};

/**
 * Update Balance
 *
 * @returns {void}
 *
 * @private
 */
var updateBalance = function () {

    /**
     * Retrieves amount from response if available
     *
     * @param  {String} response JSON string
     *
     * @return {String|Boolean} Amount or false if unavailable
     *
     * @private
     */
    var getAmount = function (response) {

        var data = {};
        try {
            data = JSON.parse(response);
        } catch (e) {
            data.amount = false;
        }
        return data.amount;
    };

    /**
     * Function called upon request completion
     *
     * @param  {Object} response Response
     *
     * @return {void}
     *
     * @private
     */
    var onComplete = function (response) {

        var amount = getAmount(response.responseText);
        if (amount) {
            setBalance(amount);
        }
    };

    /**
     * Determines which CUST action name to use to get user balance.
     *
     * @return {String}
     *
     * @private
     */
    var getBalanceAction = function getBalanceAction() {
        return 'https:' === document.location.protocol ? balanceSslAction : balanceAction;
    };

    jQuery.ajax({
        url: '/bal/' + PreferencesProvider.getLocale() + '?action=' + getBalanceAction() + '&external=false',
        dataType: 'json',
        complete: onComplete,
    });
};

/**
 * Checks if MyAccount page load module is enabled.
 *
 * @returns {Boolen}
 *
 * @private
 */
var isPageLoadModuleEnabled = function isPageLoadModuleEnabled() {
    if (window.MyAccount &&
        window.MyAccount.pageLoadModuleEnabled &&
        window.MyAccount.pageLoadModuleEnabled.toString() === 'true') {
        return window.MyAccount.pageLoadModuleEnabled.toString() === 'true';
    }
    return false;
};

/**
 * Checks if login requires a refresh.
 *
 * @TODO: This is a temporal patch which is covered in EMA-664.
 * This component will be cleaned up in the next few phases and
 * these feature switches removed.
 *
 * @returns {Boolen}
 *
 * @private
 */
var isLoginWithoutRefreshEnabled = function isLoginWithoutRefreshEnabled() {
    // This fixes https://jira.willhillatlas.com/browse/SLIP-3781 (.es and .it don't have this window.MyAccount object)
    if (!window.MyAccount) {
        return true;
    }

    if (window.MyAccount &&
        window.MyAccount.loginWithoutRefreshEnabled &&
        window.MyAccount.loginWithoutRefreshEnabled.toString() === 'true') {

        return window.MyAccount.loginWithoutRefreshEnabled.toString() === 'true';
    }

    return false;
};

/**
 * Performs actions required once authentication state is established.
 *
 * @returns {void}
 */
var loginDetermined = function () {

    window.WH.messageBus.publish(MessageBusTopics.LOGIN_JS_LOGIN_DETERMINED);

    if (!isLoginWithoutRefreshEnabled()) {

        bindLoginEvents();
    }
};

/**
 * Provides the logic to the login form.
 *
 * @returns {void}
 *
 * @private
 */
var bindLoginEvents = function () {

    /**
     * Show the appropriate container if the user is logged or not
     *
     * @param content
     *
     * @returns {void}
     *
     * @private
     */
    var toggleUserDetails = function (content) {

        /**
         * Toggle the visibility of the login/logged out details
         *
         * @returns {void}
         *
         * @private
         */
        var displayLoginDetails = function () {

            if (whx.authenticated === 'Y') {
                hideLoggedBox(content);
            } else {
                showLoggedBox(content);
            }
        };

        /**
         * Sets the account details in the appropriated form
         *
         * @returns {void}
         *
         * @private
         */
        var populateAccountDetails = function () {

            if (!userLogged()) {
                return;
            }

            /**
             * Perform the DOM manipulation needed to populate the form.
             *
             * @returns {void}
             *
             * @private
             */

            var populate = function () {

                /**
                 * Updates account elements
                 *
                 * @returns {void}
                 *
                 * @private
                 */

                /* jshint -W071 */
                var updateAccount = function () {

                    /**
                     * Handle last Login container & value update
                     *
                     * @returns {void}
                     *
                     * @private
                     */

                    var handleLastLogin = function (lastLogin) {

                        var lastLoginContainer = jQuery('.account__last');

                        if (!lastLogin) {
                            lastLoginContainer.hide();
                        } else {
                            lastLoginContainer.find('.value').text(lastLogin);
                        }
                    };

                    handleLastLogin(whx.data.obLastLogin);
                    jQuery('.account__name')
                        .text(whx.data.obGreetingLabel + ' ' + whx.data.obClientName);
                    jQuery('.account__id').append(whx.data.obAccountNumber);


                    var isProminentLoginEnabled = function () {
                        if (window.MyAccount !== undefined &&
                            window.MyAccount.prominentLoginEnabled !== undefined) {
                            return window.MyAccount.prominentLoginEnabled.toString() === 'true';
                        }
                        return false;
                    };

                    if (!isProminentLoginEnabled()) {
                        jQuery('.account-tab__button .icon-account-nli')
                            .removeClass('icon-account-nli')
                            .addClass('icon-accountLI');
                    }

                };
                /* jshint +W071 */

                updateBalance();

                if (!isPageLoadModuleEnabled()) {
                    updateAccount();
                    jQuery('#joinLink').hide();
                }

                jQuery('.header__balance-refresh').show();
                jQuery('#logged .messages .count').html(whx.data.obMessageNumber);

            };

            populate();
        };

        validateWhx();
        populateAccountDetails();

        if (!isPageLoadModuleEnabled()) {
            displayLoginDetails();
        }
    };

    /**
     * Creates and submits the post form to OB
     *
     * @param   {Object}  event
     *
     * @returns {void}
     *
     * @private
     */
    var doLogin = function (event) {

        /**
         * Creates and submits the user login form
         *
         * @param   {String}    username
         * @param   {String}    password
         *
         * @returns {void}
         *
         * @private
         */
        var submitLogin = function (username, password) {

            /**
             * Creates the login form
             *
             * @param   {String}    username
             * @param   {String}    password
             *
             * @returns {HTMLElement} login form
             *
             * @private
             */
            var loginForm = function (username, password) {

                /**
                 * Creates the form element
                 *
                 * @param   {String} name
                 * @param   {String | Integer} value
                 *
                 * @returns {HTMLElement} form
                 *
                 * @private
                 */
                var createFormElement = function (name, value) {

                    var formElement = document.createElement('input');

                    formElement.setAttribute('type', 'hidden');
                    formElement.setAttribute('name', name);
                    formElement.setAttribute('value', value);

                    return formElement;
                };

                /**
                 * Creates the form
                 *
                 * @param {String}  action
                 * @param {String}  additionalParams
                 *
                 * @returns {HTMLElement}
                 *
                 * @private
                 */
                var createForm = function (action, additionalParams) {

                    var form = document.createElement('form');

                    form.setAttribute('method', 'post');
                    form.setAttribute('action', action);
                    form.insertAdjacentHTML('beforeend', additionalParams);

                    jQuery(form)
                        .find('input[name=target_page]')
                        .val(document.URL);

                    return form;
                };

                /**
                 * Create post form
                 *
                 * @param   {String} username
                 * @param   {String} password
                 *
                 * @returns {HTMLElement}
                 *
                 * @private
                 */
                var createPostForm = function (username, password) {

                    /**
                     * Adds the remember me param to the form if checked
                     *
                     * @param   {HTMLElement} form
                     *
                     * @returns {HTMLElement}
                     *
                     * @private
                     */
                    var addRememberMeElement = function (form) {

                        if (jQuery('input#rememberMe').is(':checked')) {
                            var formRememberMe = createFormElement('remember_me', 1);
                            form.appendChild(formRememberMe);
                            rememberMeAction(username);
                        } else {
                            rememberMeAction('');
                        }

                        return form;
                    };

                    var form = createForm(whx.data.obFormAction, whx.data.obFormHidden);

                    form = addRememberMeElement(form);
                    form.appendChild(createFormElement(whx.data.obUsernameName, username));
                    form.appendChild(createFormElement(whx.data.obPasswordName, password));

                    return form;
                };

                return createPostForm(username, password);
            };

            /**
             * Execute the post form
             *
             * @param   {HTMLElement} form
             *
             * @returns {void}
             *
             * @private
             */
            var doPost = function (form) {

                document.body.appendChild(form);
                form.submit();
            };

            var form = loginForm(username, password);
            doPost(form);
        };

        /**
         * Logs in a user
         *
         * @param   {String} username
         * @param   {String} password
         *
         * @returns {void}
         *
         * @private
         */
        var loginUser = function (username, password) {

            if (!userLogged()) {
                submitLogin(username, password);
            }
        };

        event.preventDefault();

        var username = jQuery('input[name=username]')
            .val();
        var password = jQuery('input[name=password]')
            .val();

        loginUser(username, password);
    };

    /**
     * Display the login detail box and closes the burger menu if opened.
     *
     * @returns {void}
     *
     * @private
     */
    var showLoginDetails = function () {

        var expanded = !jQuery(this)
            .hasClass('-expanded');

        jQuery(this)
            .toggleClass('-expanded', expanded);
        jQuery('#account-section > .account')
            .toggleClass('-expanded', expanded);
        jQuery('#header')
            .find('.productnavigation-button-selector.-expanded')
            .trigger('click');
    };

    /**
     * Binds events
     *
     * @returns {void}
     *
     * @private
     */
    var bindEvents = function () {

        jQuery('#header').find('.header__balance').hide().removeClass('-show');
        jQuery('#header').find('.header__balance-refresh').hide();
        jQuery('#header').find('.header__balance-refresh').on('click', updateBalance);

        /**
         * Binding for account-ported functions.
         * @returns {void}
         * @private
         */
        var accountBinding = function () {

            if (shouldBindLogin(window)) {
                jQuery('#account')
                    .find('button[type=submit]')
                    .on('click', doLogin);
            }

            toggleUserDetails(jQuery('.account'));
        };

        if (!isPageLoadModuleEnabled()) {
            jQuery('#header').find('.account-tab__button').on('click', showLoginDetails);
            accountBinding();
        }

    };

    bindEvents();
    bindLogout();
};

/**
 * Determine whether or not the login should be bound.
 * window.WH.myaccount.enabled controls this.
 * @param {Object} window Global object.
 * @returns {boolean}
 * @private
 */
var shouldBindLogin = function (window) {

    return !(window.WH && window.WH.myaccount && window.WH.myaccount.enabled);
};

/**
 * Loop till the window.WHX is set. That obj is returned by OB.
 *
 * @returns {void}
 *
 * @private
 */
var waitForGlobalVar = function () {

    whx = window.WHX;

    if (typeof whx === 'undefined') {
        setTimeout(waitForGlobalVar, 100);
    } else {
        loginDetermined();
    }
};

/**
 * Bind to the logout link.
 *
 * @returns {void}
 * @private
 */
var bindLogout = function () {

    jQuery('#logged a.account__logout').on('click', onLogout);
};

/**
 * Actions to perform on logout.
 *
 * @returns {void}
 * @private
 */
var onLogout = function () {

    window.WH.messageBus.publishSticky(MessageBusTopics.BETSLIP_SLIP_CLEAR);
};

/**
 * Listen to pub/sub topics.
 *
 * @private
 * @returns {void}
 */
var addListeners = function addListeners() {

    if (window.WH && window.WH.messageBus) {

        if (!isLoginWithoutRefreshEnabled()) {

            window.WH.messageBus.subscribe(MessageBusTopics.REFRESH_BALANCE, updateBalance);

            window.WH.messageBus.subscribe(MessageBusTopics.SHOW_LOGIN, showLogin);

            window.WH.messageBus.subscribe(MessageBusTopics.SET_BALANCE, function (topic, balance) {

                setBalance(balance);
            });
        }

        window.WH.messageBus.subscribe(MessageBusTopics.SHOW_DEPOSIT, function (topic, data) {

            showDeposit(data);
        });

        window.WH.messageBus.subscribe(MessageBusTopics.SHOW_WITHDRAW, function (topic, data) {

            showWithdraw(data);
        });
    }
};

/**
 * Trigger a native scheme.
 *
 * @param {String} scheme Url to open.
 * @returns {void}
 * @private
 */
var triggerNativeScheme = function triggerNativeScheme(scheme) {

    var frame = document.createElement('iframe');
    frame.src = scheme;
    document.body.appendChild(frame);

    window.setTimeout(function () {
        document.body.removeChild(frame);
    }, 2000);
};

/**
 * Trigger the deposit function.
 *
 * @returns {void}
 * @private
 */
var showDeposit = function showDeposit(data) {

    if (DeviceDetector.isNative()) {

        triggerNativeScheme('nw-deposit://');
    } else {

        if (!isLoginWithoutRefreshEnabled()) {

            window.WH.messageBus.publish(MessageBusTopics.MY_ACCOUNT_OVERLAY_SHOW_DEPOSIT, data);
        }
    }
};

/**
 * Trigger the withdraw function.
 *
 * @returns {void}
 * @private
 */
var showWithdraw = function showWithdraw() {

    if (DeviceDetector.isNative()) {

        triggerNativeScheme('nw-withdraw://');
    } else {

        if (!isLoginWithoutRefreshEnabled()) {

            jQuery('#withdrawLink').trigger('click');
        }
    }
};

/**
 * Show the login dialog if it's hidden.
 * It won't work for the native apps because they have their own native login forms.
 *
 * @returns {void}
 * @private
 */
var showLogin = function showLogin() {

    var loginButton = document.querySelector('#header .account-tab__button:not(.-expanded)');
    if(loginButton) {
        loginButton.click();
    }
};

/**
 * Set the balance to a specific value.
 *
 * @param {String} balance Balance to set.
 * @returns {void}
 * @private
 */
var setBalance = function setBalance(balance) {

    jQuery('#header').find('.header__balance')
        .addClass('-show')
        .html(balance)
        .show();
};

/**
 * Waits for messagebus to be ready, then subscribes to login messages
 *
 * @returns {void}
 *
 * @private
 */
var addLoginListeners = function addLoginListeners() {

    /**
     * Wrapper around the two subscribe functions we want to call
     *
     * @returns {void}
     *
     * @private
     */
    var subscribe = function subscribe() {

        nativeLoginListener();
        webLoginListener();
    };

    subscribe();
};
/**
 * Listens to the native login event
 *
 * @returns {void}
 *
 * @private
 */
var nativeLoginListener = function nativeLoginListener() {

    window.WH.messageBus.subscribe(MessageBusTopics.NATIVE_LOGIN, overwriteLoginDetails);
};

/**
 * Listens for the refreshless login message for web, and updates the WHX object when we get the message
 *
 * @returns {void}
 *
 * @private
 */
var webLoginListener = function webLoginListener() {

    window.WH.messageBus.subscribe(MessageBusTopics.GOT_LOGIN_STATE, updateWhxObject);
};

/**
 * Updates the whx login status object based on data from the gotLoginState message
 *
 * @param {String} eventName    This will always be 'gotLoginState', we don't care about it
 * @param {Object} loginData    This is the object passed from myaccount's ajax login flow, it should contain the
 *                              login status and account details of the customer
 *
 * @returns {void}
 *
 * @private
 */
var updateWhxObject = function updateWhxObject(eventName, loginData) {

    if (_.isObject(loginData) && _.isObject(loginData.data) && _.size(loginData.data) > 0) {
        whx = {};
        whx.authenticated = getAuthenticationStatus(loginData);
        whx.data = loginData.data;
        window.WHX = whx;
    }
};

/**
 * Checks the isLoggedIn param on the gotLoginState message
 *
 * @param {Object}  loginData   Message passed from myaccount with the gotLoginState message
 *
 * @returns {string}            Whether or not the user is authenticated (Y or N)
 *
 * @private
 */
var getAuthenticationStatus = function getAuthenticationStatus(loginData) {

    return (loginData.isLoggedIn) ? 'Y' : 'N';
};

/**
 * Waits for the cust_login cookie to have a value or a 60 second timeout before appending login script
 *
 * @param {String} loginUrl The url of the script to append
 * @param {Number} iteration The iteration number, used to prevent an infinite loop
 *
 * @returns {number}
 *
 * @private
 */
var safeAppendLoginScript = function safeAppendLoginScript(loginUrl, iteration) {

    if (!/cust_auth=[^;]/.test(document.cookie) && iteration < 60) {

        return setTimeout(function () {

            iteration++;
            safeAppendLoginScript(loginUrl, iteration);
        }, 1000);
    }

    window.appendScriptTag(loginUrl, true);
};

/**
 * Gets the login URL
 *
 * @returns {string}
 *
 * @private
 */
var getLoginUrl = function () {

    var loginURL = URLProvider.login.getLoginURL({
        host: window.location.host,
        lang: window.WH.sportsbook.lang,
    });

    window.loginURL = window.loginURL || loginURL;

    return loginURL;
};

/**
 * Overwrites the login details and re-inits the betslip
 *
 * @param {String} topic
 * @param {Boolean} value True if the user is authenticated. False otherwise
 *
 * @returns {void}
 *
 * @private
 */
var overwriteLoginDetails = function overwriteLoginDetails(topic, value) {

    var loginUrl = getLoginUrl();

    if (parseInt(value, 10) !== 0) {

        safeAppendLoginScript(loginUrl, 0);
    } else {

        onLogout();
    }

    // reset whxx
    whx = null;
    window.WHX = undefined;
    // reinit login
    waitForGlobalVar();

    DeferredDependencies.init();
    BetSlipAdapter.reInit();
};

/**
 * Public functions.
 *
 * @type {Object}
 */
var api = {

    module: {
        shouldBindLogin: shouldBindLogin,
    },

    /**
     * Determine whether the user is logged in.
     * @returns {Boolean}
     * @public
     */
    isLoggedIn: function () {

        return userLogged();
    },

    /**
     * Get user balance if logged in, else return 0
     *
     * @returns {Number}
     *
     * @public
     */
    getUserBalance: function getUserBalance() {

        if (userLogged()) {

            return parseFloat(whx.data.obBalanceAmount);
        }

        return 0;
    },

    /**
     * Adjusts the height and width
     *
     * @returns {void}
     *
     * @public
     */
    adjustHeightWidth: function () {

        var width = jQuery(this.currItem.el)
            .attr('data-width');
        var height = jQuery(this.currItem.el)
            .attr('data-height');
        var container = jQuery(this.container)
            .find('div.mfp-content');

        setDimension('width', width, container);
        setDimension('height', height, container);
    },

    /**
     * Entry point. Waits for WHX object to be set and performs the events binding.
     *
     * @param {Object} PreferencesProvider
     *
     * @returns {void}
     *
     * @public
     */
    init: function (PreferencesProvider) {

        if (!isLoginWithoutRefreshEnabled()) {

            injectDependencies(PreferencesProvider);
        }

        waitForGlobalVar();

        if (!isLoginWithoutRefreshEnabled()) {

            updateLoginForm();
        }

        addListeners();
        addLoginListeners();
    },
};

export default api;
